'use client'
import { Link } from '~/components/ui/link'
import { useState } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import { useMediaQuery } from '~/hooks/use-media-query'
import SafeHTML from '~/components/ui/safe-html'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { useLocalizedNavigation } from '~/hooks/use-localized-navigation'
import { Image } from '~/components/ui/image'

export interface FooterColumn {
  id: string
  sort?: number
  isShow?: boolean
  group?: string
  translations: {
    title: string
  }[]
  pages?: {
    id: number
    translations: {
      label?: string
      url?: string
      languages_code?: string
    }[]
  }[]
  footer_item_id?: {
    translations: {
      label?: string
      url?: string
    }[]
  }[]
}

interface SocialLink {
  id: number
  url: string
  icon_svg: string
}

interface FooterNavProps {
  columns: FooterColumn[]
  contactInfo: {
    email: string
    phone: string
    address: string
    name: string
  }
  socialLinks: SocialLink[]
}

export function FooterNav({ columns, contactInfo, socialLinks }: FooterNavProps) {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const { getFullUrl } = useLocalizedNavigation()
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({})
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const MHGURL = process.env.NEXT_PUBLIC_PORTAL_URL

  const toggleColumn = (id: string) => {
    if (isDesktop) return
    setOpenStates((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const getTranslationForLocale = (translations: any[], fallbackLocale = 'vi') => {
    return (
      translations.find((t) => t.languages_code === locale) ||
      translations.find((t) => t.languages_code === fallbackLocale) ||
      translations[0]
    )
  }

  const getProcessedUrl = (url: string | undefined) => {
    if (!url) return '#'
    // Nếu là link ngoài hoặc anchor, giữ nguyên
    if (url.startsWith('http') || url.startsWith('#')) return url
    // Nếu không, thêm prefix
    return getFullUrl(url)
  }

  return (
    <div className="flex flex-col justify-between space-y-8 px-0 py-8 text-[#F2F2F2] md:flex-row md:gap-5 md:space-y-0 lg:gap-15">
      <div className="md:max-w-[26%] md:flex-[40%]">
        <h3 className="mb-4 font-lora text-[16px] font-medium leading-7 text-[#F2F2F2] sm:text-[18px] sm:leading-[32px]">
          {contactInfo?.name}
        </h3>
        <p className="mb-2 font-lexend text-sm font-light leading-[22px] sm:text-[16px] sm:leading-[24px]">
          {contactInfo?.address}
        </p>
        <p className="text-decoration-none mb-2 font-lexend text-sm font-light leading-[22px] sm:text-[16px] sm:leading-[24px]">
          {t('common.contact')}: {contactInfo.phone}
        </p>
        <p className="text-decoration-none mb-2 font-lexend text-sm font-light leading-[22px] sm:text-[16px] sm:leading-[24px]">
          {t('footer.email')}: {contactInfo.email}
        </p>
        {isDesktop && (
          <>
            <div>
              <div className="flex space-x-4">
                {socialLinks?.map((link) => (
                  <Link
                    key={link.id}
                    href={MHGURL + link.url}
                    className="hover:text-white"
                    aria-label={MHGURL + link.url}
                    target="_self"
                  >
                    <SafeHTML
                      html={link.icon_svg}
                      className="[&>svg]:h-full [&>svg]:w-full [&>svg]:fill-current"
                    />
                  </Link>
                ))}
              </div>
            </div>
            <div className={`mt-[34px]`}>
              <Link
                href={'http://online.gov.vn/Website/chi-tiet-136662'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image src={`/images/bo_cong_thuong.png`} width={176} height={67} alt={''}></Image>
              </Link>
            </div>
          </>
        )}
      </div>

      {columns
        .filter((r) => r.group !== 'other')
        .sort((a: any, b: any) => a.sort - b.sort)
        .map((column) => {
          const items = column.pages || column.footer_item_id || []

          return (
            <div
              key={column.id}
              className={`${!isDesktop || column.id !== 'other' ? 'md:flex-[20%]' : 'md:flex-[40%]'}`}
            >
              {column.id !== 'other' ? (
                <>
                  <h3
                    onClick={() => toggleColumn(column.id)}
                    className="mb-0 flex cursor-pointer items-center justify-between font-lora text-[16px] font-medium leading-[22px] text-[#F2F2F2] sm:text-[18px] sm:leading-[24px] md:cursor-default"
                  >
                    {column.translations[0]?.title}
                    <FaChevronDown
                      className={`transform transition-transform md:hidden ${
                        openStates[column.id] ? 'rotate-180' : ''
                      }`}
                    />
                  </h3>
                  <ul
                    className={`space-y-2 ${!openStates[column.id] && !isDesktop ? 'hidden' : ''}`}
                  >
                    {items.map((item, index) => {
                      const itemTranslation = getTranslationForLocale(item.translations)
                      return (
                        <li
                          key={index}
                          className="mt-2 font-lexend text-sm font-light leading-[22px] text-[#F2F2F2] hover:text-[#896145] sm:text-[16px] sm:leading-[24px]"
                        >
                          <Link
                            href={MHGURL + itemTranslation?.url || '#'}
                            aria-label={MHGURL + itemTranslation?.label}
                            target="_self"
                          >
                            {itemTranslation?.label}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </>
              ) : (
                <>
                  {items.map((item, index) => {
                    const itemTranslation = getTranslationForLocale(item.translations)
                    return (
                      <div
                        key={index}
                        className={
                          `${index !== 0 ? 'mt-8' : ''}` +
                          ' font-lexend text-sm font-light leading-[22px] text-[#F2F2F2] hover:text-[#896145] sm:text-[16px] sm:leading-[24px]'
                        }
                      >
                        <Link
                          href={MHGURL + itemTranslation?.url || '#'}
                          aria-label={MHGURL + itemTranslation?.label}
                          target="_self"
                        >
                          {itemTranslation?.label}
                        </Link>
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          )
        })}

      {!isDesktop && (
        <>
          <div>
            <div>
              <div className="flex space-x-4">
                {socialLinks?.map((link) => (
                  <Link
                    key={link.id}
                    href={MHGURL + link.url}
                    className="hover:text-white"
                    aria-label={MHGURL + link.url}
                    target="_self"
                  >
                    <SafeHTML
                      html={link.icon_svg}
                      className="[&>svg]:h-full [&>svg]:w-full [&>svg]:fill-current"
                    />
                  </Link>
                ))}
              </div>
            </div>
            <div className={`mt-[34px]`}>
              <Link
                href={'http://online.gov.vn/Website/chi-tiet-136662'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image src={`/images/bo_cong_thuong.png`} width={176} height={67} alt={''}></Image>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
